import type { CameraDevice } from '@renderer/types/utils'
import { isDualCameraEnabled } from './cameraSelection'

/**
 * Serial numbers as CHROMIUM sees them.
 *
 * node-usb frequently cannot read a camera's iSerialNumber on Windows — it needs to
 * open the device, which fails with LIBUSB_ERROR_NOT_SUPPORTED (no WinUSB driver
 * bound) or LIBUSB_ERROR_ACCESS (body already claimed by a WebUSB session). When that
 * happens, main-process enumeration returns same-series twins as indistinguishable
 * bare vendor/product entries and the settings dropdown cannot offer a serial-qualified
 * selection for the second body.
 *
 * Chromium, however, reads string descriptors during its own USB enumeration without
 * opening the device. New packaged mains forward the `select-usb-device` deviceList
 * (serials included) on the `usb-session-device-list` channel every time a WebUSB
 * request runs — so after the FIRST camera connect this cache knows the serial of
 * every attached camera body, including the claimed one. Old packaged mains never send
 * the event and this module stays empty (legacy behavior everywhere).
 */

export interface SessionUsbDevice {
  vendorId: number
  productId: number
  serialNumber?: string
  productName?: string
  manufacturerName?: string
  deviceClass?: number
  deviceSubclass?: number
}

let sessionDevices: SessionUsbDevice[] = []
let initialized = false
const subscribers = new Set<() => void>()

/**
 * Register the IPC listener once per renderer lifetime. The preload's generic `on`
 * has no working unsubscribe (its removeListener wraps the callback in a fresh
 * function), so this must never be called per-component — hence the module guard.
 */
export function initUsbSessionDeviceListener(): void {
  if (initialized) return
  if (!window.electron || typeof window.api?.on !== 'function') return
  initialized = true
  window.api.on<SessionUsbDevice[]>('usb-session-device-list', (list) => {
    if (!Array.isArray(list)) return
    sessionDevices = list.filter(
      (d) => d && typeof d.vendorId === 'number' && typeof d.productId === 'number'
    )
    subscribers.forEach((notify) => notify())
  })
}

/** Subscribe to cache updates; returns an unsubscribe function. */
export function subscribeUsbSessionDevices(notify: () => void): () => void {
  subscribers.add(notify)
  return () => {
    subscribers.delete(notify)
  }
}

/**
 * Fill in serials that main-process enumeration could not read, using the serials
 * Chromium reported for the same vendor/product model.
 *
 * Serial→body assignment inside one model group is arbitrary (there is no shared key
 * between a node-usb row and a Chromium row beyond vendor/product), and that is fine:
 * the selection value only needs to target ONE distinct physical body, whichever row
 * it is displayed on. If Chromium saw more bodies of a model than node-usb did (its
 * enumeration also works while a body is claimed), synthetic rows are appended so
 * every physical body is selectable.
 *
 * No-op unless the dual_camera flag is on — single-camera booths keep byte-identical
 * dropdown contents.
 */
export function enrichUsbCamerasWithSessionSerials(cameras: CameraDevice[]): CameraDevice[] {
  if (!isDualCameraEnabled() || sessionDevices.length === 0 || cameras.length === 0) {
    return cameras
  }

  const modelKey = (vendorId?: number, productId?: number): string => `${vendorId}:${productId}`

  const sessionSerialsByModel = new Map<string, string[]>()
  for (const device of sessionDevices) {
    if (!device.serialNumber) continue
    const key = modelKey(device.vendorId, device.productId)
    const serials = sessionSerialsByModel.get(key) ?? []
    if (!serials.includes(device.serialNumber)) serials.push(device.serialNumber)
    sessionSerialsByModel.set(key, serials)
  }
  if (sessionSerialsByModel.size === 0) return cameras

  const enriched: CameraDevice[] = []
  const consumedByModel = new Map<string, Set<string>>()

  for (const camera of cameras) {
    const key = modelKey(camera.vendorId, camera.productId)
    const known = sessionSerialsByModel.get(key)
    if (!known) {
      enriched.push(camera)
      continue
    }
    const consumed = consumedByModel.get(key) ?? new Set<string>()
    consumedByModel.set(key, consumed)

    if (camera.serialNumber) {
      consumed.add(camera.serialNumber)
      enriched.push(camera)
      continue
    }
    const nextFree = known.find((serial) => !consumed.has(serial))
    if (nextFree) consumed.add(nextFree)
    enriched.push(nextFree ? { ...camera, serialNumber: nextFree } : camera)
  }

  // Bodies Chromium saw but node-usb missed entirely (e.g. claimed by the running
  // primary connection): append them so the second body is selectable at all.
  for (const [key, serials] of sessionSerialsByModel) {
    const consumed = consumedByModel.get(key)
    const template = cameras.find((c) => modelKey(c.vendorId, c.productId) === key)
    if (!template) continue
    for (const serial of serials) {
      if (consumed?.has(serial)) continue
      enriched.push({ ...template, serialNumber: serial })
    }
  }

  return enriched
}
